import { chainflipLinks } from '@chainflip/utils/consts';
import classNames from 'classnames';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { Link } from '@/shared/components/atoms/Link';
import { BANNER_STATUS_QUERY } from '@/shared/graphql/cache/environment';
import { getLatestBlocksQuery } from '@/shared/graphql/explorer/queries';
import { type App } from '@/shared/graphql/generated/cache/graphql';
import { getLastBlockQuery } from '@/shared/graphql/lp/events';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import CheckCircle from '@/shared/icons/flip-ui-kit/large/CheckCircle';
import ConstructionIcon from '@/shared/icons/flip-ui-kit/large/Construction';
import InformationIcon from '@/shared/icons/flip-ui-kit/large/Information';

const APP_LAG_THRESHOLD_MINUTES = {
  EXPLORER: 2,
  LP: 2,
} as const;

const ICON = {
  INFO: <InformationIcon />,
  WARNING: <ConstructionIcon />,
  SUCCESS: <CheckCircle />,
} as const;

const STYLE = {
  INFO: 'text-cf-blue-2 bg-[#1C2F4A]',
  WARNING: 'text-cf-orange-1 bg-[#463719]',
  SUCCESS: 'text-cf-green-1 bg-[#254334]',
} as const;

const LINK_COLOR = {
  INFO: 'blue',
  WARNING: 'orange',
  SUCCESS: 'green',
} as const;

export default function Banner({ app }: { app: App }): React.JSX.Element | null {
  const { data } = useGqlQuery(BANNER_STATUS_QUERY, {
    context: { clientName: 'statechainCache' },
    variables: { app },
  });
  const { data: blocksQuery } = useGqlQuery(getLatestBlocksQuery, {
    refetchInterval: 30_000,
    enabled: ['EXPLORER', 'LP'].includes(app),
  });
  const { data: lpBlocksQuery } = useGqlQuery(getLastBlockQuery, {
    refetchInterval: 30_000,
    enabled: app === 'LP',
    context: { clientName: 'lpProcessor' },
  });

  const lastBlockTimestampExplorer = new Date(
    blocksQuery?.latestBlocks?.nodes.at(0)?.timestamp ?? Date.now(),
  ).getTime();

  const lastBlockTimestampLP = new Date(
    lpBlocksQuery?.latestBlocks?.nodes.at(0)?.timestamp ?? Date.now(),
  ).getTime();

  const difference =
    app === 'LP'
      ? differenceInMinutes(
          new Date(),
          new Date(Math.min(lastBlockTimestampLP, lastBlockTimestampExplorer)),
        )
      : differenceInMinutes(new Date(), new Date(lastBlockTimestampExplorer));

  const isLaggy =
    (app === 'LP' || app === 'EXPLORER') && difference >= APP_LAG_THRESHOLD_MINUTES[app];

  const lagMessage =
    isLaggy && `Indexing is currently experiencing lag. Updates are delayed by ${difference} min.`;

  const row = data?.banner?.nodes[0];

  const message = row?.message ?? lagMessage;
  const type = row?.type || (lagMessage && 'WARNING');
  const linkUrl = row?.linkUrl;
  const linkText = row?.linkText;
  const showDiscordLink = Boolean(row?.message) && !linkUrl;

  if (!message || !type) return null;

  const displayedMessage = message.endsWith('.') ? message : `${message}.`;

  return (
    <div
      ref={(el) => {
        if (el) {
          document.documentElement.style.setProperty('--banner-height', `${el.clientHeight}px`);
        } else {
          document.documentElement.style.setProperty('--banner-height', '0px');
        }
      }}
      className={classNames(
        'fixed top-0 z-[9999] flex w-full items-center justify-center py-[5px] text-center text-12',
        STYLE[type],
      )}
    >
      <div className="container flex items-center justify-center space-x-1 font-aeonikMedium">
        <div>{ICON[type]}</div>
        <div>
          {displayedMessage}
          {linkUrl ? (
            <>
              {' '}
              <Link
                href={linkUrl}
                target="_blank"
                underline
                color={LINK_COLOR[type]}
                className="inline"
              >
                {linkText ?? 'Read more.'}
              </Link>
            </>
          ) : (
            showDiscordLink && (
              <>
                {' '}
                Head to{' '}
                <Link
                  href={chainflipLinks.discord}
                  target="_blank"
                  underline
                  color={LINK_COLOR[type]}
                  className="inline"
                >
                  our discord
                </Link>{' '}
                for more updates.
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
